import {
  NodeInputType,
  type InputValue,
  type NodeParam,
} from '@/types/node-types';

/**
 * Coerce a chat-supplied field value into the param's declared type.
 *
 * Chat tools send every value as a string, so a boolean toggle arrives as
 * "true" and a strict `value === true` check silently stays Off. Non-strings,
 * unknown params and unrecognized tokens pass through untouched.
 */
export function coerceChatValueToParamType(
  param: NodeParam | undefined,
  value: unknown,
): InputValue {
  if (typeof value !== 'string') return value as InputValue;
  if (!param) return value;

  const validTypes = param.valid_types ?? [];

  const isBool =
    validTypes.includes('bool') ||
    param.display_modal === NodeInputType.BINARY_TOGGLE ||
    param.display_modal === NodeInputType.BOOLEAN;
  if (isBool) {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
    return value;
  }

  const isFloat =
    validTypes.includes('float') || param.display_modal === NodeInputType.FLOAT;
  const isInt =
    validTypes.includes('int') || param.display_modal === NodeInputType.INTEGER;
  if (isFloat || isInt) {
    if (value.trim() === '') return value;
    const num = Number(value);
    // Reject NaN and ±Infinity ("Infinity", "1e309", …) rather than persist them.
    if (!Number.isFinite(num)) return value;
    return isFloat ? num : Math.trunc(num);
  }

  return value;
}
